import { z } from 'zod'
import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { toast } from 'react-toastify'
import { parseEther, formatEther, type Hex } from 'viem'
import { zodResolver } from '@hookform/resolvers/zod'
import { useWritePepperContract } from '@/hooks/pepper-contract/useWritePepperContract'
import { useStaking2aAddress } from '@/hooks/staking2a-contract/useStaking2aContract'
import { useWriteStaking2aContract } from '@/hooks/staking2a-contract/useWriteStaking2aContract'
import { usePepperContractAddress } from '@/hooks/pepper-contract/usePepperContract'
import { waitForTransactionReceipt } from '@/utils/wagmi/waitForTransactionReceipt'
import { usePepperBalance } from '@/hooks/pepper-contract/usePepperBalance'
import { usePepperUserAllowanceOnStaking } from '@/hooks/pepper-contract/usePepperUserAllowanceOnStaking'
import { useCurrencyFormatter } from '@/hooks/helpers/useCurrencyFormatter'
import { useStaking2aUserStakeData } from '@/hooks/staking2a-contract/useStaking2aUserStakeData'
import { Button } from '@/components/ui/Button'
import { Skeleton } from '@/components/ui/Skeleton'
import { AmountForm } from './AmountForm'
import { logger } from '@/observability/logger'
import { minBigInt } from '@/utils/math/bigint'
import Info from '@/assets/icons/Info.svg?react'
import {
	ResponsiveDialog,
	ResponsiveDialogContent,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
	type ResponsiveDialogProps
} from '@/components/ui/ResponsiveDialog'
import { testIdFactory } from '@/utils/test-id-factory.ts'
import { useTracking } from '@/providers/TrackingProvider'

const stakingFormSchema = (balance: bigint, votingCap: bigint, isVotingAllowed: boolean) =>
	z.object({
		amount: z
			.string()
			.regex(/^\d*\.?\d*$/, 'Invalid number')
			.refine(x => Number(x) >= 1, 'Must be at least 1 PEPPER')
			.refine(x => Number(x) <= Number(formatEther(votingCap)), "Amount exceeds the poll's voting cap")
			.refine(
				x => (!isVotingAllowed ? Number(x) <= Number(formatEther(balance)) : true),
				'Amount exceeds your PEPPER balance'
			)
	})

export function StakeAndVoteDialog({
	selectedOption,
	userUnlockedTokens,
	votingCap,
	onSubmit,
	onVote,
	...dialogProps
}: Omit<ResponsiveDialogProps, 'children'> & {
	selectedOption: string
	userUnlockedTokens: bigint
	votingCap: bigint
	onSubmit: () => void
	onVote: (selectedOption: string, amountInWei: bigint) => Promise<Hex | undefined>
}) {
	// Hooks
	const writePepperContract = useWritePepperContract()
	const writeStaking2aContract = useWriteStaking2aContract()
	const stakingAddress = useStaking2aAddress()
	const pepperAddress = usePepperContractAddress()
	const { refetch: refetchUserStakedData } = useStaking2aUserStakeData()
	const {
		data: pepperBalance = 0n,
		isPending: isPendingPepperBalance,
		refetch: refetchPepperBalance
	} = usePepperBalance()
	const {
		data: pepperAllowance = 0n,
		isPending: isPendingPepperAllowance,
		refetch: refetchAllowance
	} = usePepperUserAllowanceOnStaking()
	const format = useCurrencyFormatter(3)
	const { sendEvent } = useTracking()

	// Form State
	const [isStakingAllowed, setIsStakingAllowed] = useState(false)
	const [isVotingAllowed, setIsVotingAllowed] = useState(false)
	const form = useForm({
		mode: 'onChange',
		resolver: zodResolver(stakingFormSchema(pepperBalance, votingCap, isVotingAllowed)),
		defaultValues: {
			amount: formatEther(minBigInt(votingCap, pepperBalance) ?? 0n)
		}
	})

	const amount = useWatch({ control: form.control, name: 'amount' })
	const amountInWei = amount ? parseEther(amount) : 0n
	const isLoading = isPendingPepperBalance || isPendingPepperAllowance

	// Reset approval when amount changes
	useEffect(() => {
		if (isStakingAllowed) {
			setIsStakingAllowed(false)
			setIsVotingAllowed(false)
			refetchAllowance()
		}
	}, [amountInWei])

	const isPepperAllowanceApproved = (pepperAllowance: bigint, amountToStake: bigint): boolean =>
		pepperAllowance >= amountToStake

	// Handlers
	const handlePepperAllowance = async (): Promise<void> => {
		try {
			const hash = await writePepperContract({
				functionName: 'approve',
				args: [stakingAddress, amountInWei]
			})

			await waitForTransactionReceipt({ hash })
			setIsStakingAllowed(true)
			/* TODO(DFT-1951): Remove this line form.setValue is it seems unncecessary
			 * keeping it in case we encounter any issues with the form state
			 */
			// form.setValue('amount', amount, {
			// 	shouldValidate: true
			// })
		} catch (e) {
			logger.error(e, 'Error approving Pepper allowance')
			toast.error('An error occurred while approving Pepper allowance.')
		}
	}

	const handleApprove = form.handleSubmit(async () => {
		const isApproved = isPepperAllowanceApproved(pepperAllowance, amountInWei)
		if (isApproved) {
			setIsStakingAllowed(true)

			return
		}
		await handlePepperAllowance()
	})

	const handleStake = form.handleSubmit(async () => {
		try {
			sendEvent('click_stake_pepper')
			const hash = await writeStaking2aContract({
				functionName: 'stake',
				args: [amountInWei, pepperAddress]
			})
			await waitForTransactionReceipt({ hash })
			sendEvent('stake_pepper_successful', { amount, txHash: hash })
			setIsVotingAllowed(true)
			await refetchUserStakedData()
			await refetchPepperBalance()
		} catch (e) {
			logger.error(e, 'Error staking Pepper')
			toast.error('An error occurred while staking Pepper.')
		}
	})

	const handleVote = form.handleSubmit(async () => {
		try {
			sendEvent('click_vote')
			const hash = await onVote(selectedOption, amountInWei)

			// Temp fix - Should be handled properly
			if (!hash) {
				return
			}

			sendEvent('vote_successful', { answer: selectedOption, amount, txHash: hash })
			await onSubmit()
		} catch (e) {
			logger.error(e, 'Vote error')
			toast.error(
				`An error occurred while staking Pepper for selected option ${selectedOption} and amountWei ${amountInWei}`
			)
		}
	})

	const submitButtonLabel = isVotingAllowed ? 'vote' : isStakingAllowed ? 'stake' : 'approve'

	return (
		<ResponsiveDialog {...dialogProps}>
			<ResponsiveDialogContent>
				<div className="overflow-y-auto gap-8 flex flex-col">
					<ResponsiveDialogHeader>
						<ResponsiveDialogTitle>How much Pepper do you want to stake to vote?</ResponsiveDialogTitle>
					</ResponsiveDialogHeader>
					<div className="flex gap-2 items-start md:items-center text-neutral-30">
						<Info className="w-[2.5rem] md:w-[1.5rem]" />
						<span className="text-2xl leading-none">
							{`The poll's voting cap is ${format(formatEther(votingCap))} PEPPER.`}
						</span>
					</div>
					<div>
						<AmountForm
							form={form}
							base={minBigInt(votingCap, pepperBalance) ?? 0n}
							disabled={isStakingAllowed}
						/>
					</div>
					<h4 className="text-center text-3xl text-neutral-40 leading-none flex items-center justify-center gap-2 flex-col md:flex-row">
						<span>{`${isVotingAllowed ? 'Available Staked' : 'PEPPER'} Balance:`}</span>
						{isLoading ? (
							<Skeleton className="w-[13ch] h-[30px]" />
						) : (
							<span>
								{format(formatEther(isVotingAllowed ? userUnlockedTokens : (pepperBalance ?? 0n)))}
							</span>
						)}
					</h4>
					<ResponsiveDialogFooter>
						<Button
							onClick={isVotingAllowed ? handleVote : isStakingAllowed ? handleStake : handleApprove}
							disabled={!form.formState.isValid || !amount}
							loading={form.formState.isSubmitting}
							variant="danger"
							className="py-5 px-10 leading-none capitalize"
							data-testId={testIdFactory.pepperAmountFormButton(submitButtonLabel)}
						>
							{submitButtonLabel}
						</Button>
					</ResponsiveDialogFooter>
				</div>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	)
}
